import React, { useState } from 'react';
import PropTypes from 'prop-types';

import './BrochureCommunityPrompt.scss';

/**
 * Turn what someone types into a community URL slug.
 *
 * The field asks for the community, not for a slug, so accept the name as it
 * appears on the sign ("Maple Ridge Senior Living") as well as the slug itself
 * ("maple-ridge-senior-living") and normalise both to the same thing.
 */
export const toCommunitySlug = (value) => (value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/**
 * Recovery for a brochure link that arrived with no community on it.
 *
 * The lead resource refuses an unscoped link when the same contact appears in
 * more than one of our communities, and it cannot offer to disambiguate itself:
 * doing so would tell whoever holds that URL which of our communities this
 * person toured. So the question has to be asked here, of the person who
 * already knows the answer, and the link re-requested with ?community=.
 */
const BrochureCommunityPrompt = ({ error, onSubmit }) => {
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);

  const slug = toCommunitySlug(value);
  const isEmpty = touched && !slug;

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    if (!slug) return;
    onSubmit(slug);
  };

  return (
    <div className="brochureCommunityPrompt" data-testid="brochure-community-prompt">
      <form className="brochureCommunityPrompt__card" onSubmit={handleSubmit}>
        <h1 className="brochureCommunityPrompt__title">Which community did you tour?</h1>
        <p className="brochureCommunityPrompt__body">
          This brochure link is missing its community, so we can&apos;t tell which
          of your visits it belongs to. Tell us the community and we&apos;ll open
          the right brochure.
        </p>

        <label className="brochureCommunityPrompt__label" htmlFor="brochure-community">
          Community name
          <input
            id="brochure-community"
            className="brochureCommunityPrompt__input"
            type="text"
            autoComplete="off"
            value={value}
            data-testid="brochure-community-input"
            onChange={(e) => setValue(e.target.value)}
            aria-describedby={error || isEmpty ? 'brochure-community-error' : undefined}
          />
        </label>

        {(error || isEmpty) && (
          <p className="brochureCommunityPrompt__error" id="brochure-community-error" role="alert">
            {isEmpty ? 'Please enter the community you toured.' : error}
          </p>
        )}

        <button
          className="brochureCommunityPrompt__submit"
          type="submit"
          data-testid="brochure-community-submit"
        >
          Open my brochure
        </button>
      </form>
    </div>
  );
};

BrochureCommunityPrompt.propTypes = {
  error: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};

BrochureCommunityPrompt.defaultProps = {
  error: null,
};

export default BrochureCommunityPrompt;
